import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const MAPBOX_SCRIPT_ID = 'mapbox_GL_JS';
const GOOGLE_MAPS_SCRIPT_ID = 'GoogleMapsApi';
const META_PIXEL_SCRIPT_ID = 'metaPixel';
const META_PIXEL_SCRIPT_URL = 'https://connect.facebook.net/en_US/fbevents.js';

// FALLBACK loader for the Meta Pixel.
//
// In production the pixel base code is server-rendered into <head> by
// server/renderer.js (metaPixelScriptFor). It has to be, because Meta's
// crawlers read the HTML without executing JavaScript and would otherwise
// report "no pixel found on this website". This function then finds the pixel
// already present and does nothing.
//
// It still runs for renders that never pass through that template - notably
// the webpack dev server, which serves public/index.html verbatim and leaves
// the <!--!metaPixelScript--> tag as an inert HTML comment.
//
// Every step below is guarded against the server-rendered snippet:
//   - window.fbq          - the stub the snippet already created
//   - __metaPixelInitialized - set by the snippet, so no duplicate PageView
//   - the 'metaPixel' script id - set by the snippet on its own <script>, so
//                                 fbevents.js is never appended twice
//
// The Meta loader expects window.fbq to exist before fbevents.js executes, so
// the queue is initialized first and the external script added only after.
export const initializeMetaPixel = facebookPixelId => {
  if (!facebookPixelId || typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const pixelAlreadyLoaded = typeof window.fbq?.callMethod === 'function';

  if (!window.fbq) {
    const fbq = function() {
      fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = fbq;
    window._fbq = window._fbq || fbq;
  }

  if (!window.__metaPixelInitialized) {
    window.fbq('init', facebookPixelId);
    window.fbq('track', 'PageView');
    window.__metaPixelInitialized = true;
  }

  if (!pixelAlreadyLoaded && !document.getElementById(META_PIXEL_SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = META_PIXEL_SCRIPT_ID;
    script.async = true;
    script.src = META_PIXEL_SCRIPT_URL;
    document.head.appendChild(script);
  }
};

// Mapbox is fetched the first time a map or a place search asks for it, not from every page's
// <head>: mapbox-gl.js is ~370 kB and was a synchronous script there, holding back first paint
// on pages that show no map. IncludeScripts registers the settings on each render, because the
// map and the geocoder that ask for the library have no config of their own.
//
// License information for v3.7.0 of the mapbox-gl-js library:
// https://github.com/mapbox/mapbox-gl-js/blob/v3.7.0/LICENSE.txt
const MAPBOX_GL_URL = 'https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.js';
const MAPBOX_GL_CSS_URL = 'https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.css';
let mapboxSettings = null;
const mapboxLoads = {};

const notInUse = () => Promise.reject(new Error('Mapbox is not the map provider in use'));

const appendToHead = (tagName, attributes) =>
  new Promise((resolve, reject) => {
    const el = Object.assign(document.createElement(tagName), attributes);
    el.onload = resolve;
    el.onerror = reject;
    document.head.appendChild(el);
  });

// One fetch per library, however many maps ask. A failure is forgotten so the next ask retries.
const loadOnce = (key, load) => {
  if (!mapboxLoads[key]) {
    mapboxLoads[key] = load().catch(e => {
      delete mapboxLoads[key];
      throw e;
    });
  }
  return mapboxLoads[key];
};

/**
 * Loads Mapbox GL - enough to draw a map - and sets its access token.
 *
 * @returns {Promise} resolves once window.mapboxgl is ready to use
 */
export const loadMapboxGL = () => {
  if (typeof window === 'undefined') {
    return notInUse();
  }
  if (!mapboxSettings) {
    return window.mapboxgl ? Promise.resolve() : notInUse();
  }
  return loadOnce('gl', () =>
    Promise.all([
      // Only the controls' looks depend on it, so a failed stylesheet does not hold back the map.
      appendToHead('link', {
        rel: 'stylesheet',
        href: MAPBOX_GL_CSS_URL,
        crossOrigin: 'anonymous',
      }).catch(() => {}),
      window.mapboxgl
        ? null
        : appendToHead('script', {
            id: MAPBOX_SCRIPT_ID,
            src: MAPBOX_GL_URL,
            crossOrigin: 'anonymous',
          }),
    ]).then(() => {
      if (!window.mapboxgl.accessToken) {
        window.mapboxgl.accessToken = mapboxSettings.accessToken;
      }
    })
  );
};

/**
 * Loads Mapbox GL and the Mapbox SDK, which the place search needs on top of GL.
 *
 * @returns {Promise} resolves once window.mapboxgl and window.mapboxSdk are ready to use
 */
export const loadMapboxSdk = () => {
  if (typeof window === 'undefined') {
    return notInUse();
  }
  if (!mapboxSettings) {
    return window.mapboxgl && window.mapboxSdk ? Promise.resolve() : notInUse();
  }
  return loadOnce('sdk', () =>
    Promise.all([
      loadMapboxGL(),
      // NOTE: remember to update mapbox-sdk.min.js to a new version regularly.
      // It is served from the static folder for CSP purposes.
      window.mapboxSdk
        ? null
        : appendToHead('script', {
            src: `${mapboxSettings.rootURL}/static/scripts/mapbox/mapbox-sdk.min.js`,
          }),
    ])
  );
};

/**
 * Waits for Stripe.js, which public/index.html loads async on every page.
 *
 * @returns {Promise} resolves with window.Stripe
 */
export const whenStripeLoaded = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Stripe.js needs a browser'));
  }
  if (window.Stripe) {
    return Promise.resolve(window.Stripe);
  }
  const script = document.getElementById('stripe-js');
  if (!script) {
    return Promise.reject(new Error('Stripe.js is not on the page'));
  }
  return new Promise((resolve, reject) => {
    script.addEventListener('load', () => resolve(window.Stripe));
    script.addEventListener('error', reject);
  });
};

/**
 * Include scripts (like Map Provider).
 * These scripts are relevant for whole application: location search in Topbar and maps on different pages.
 * However, if you don't need location search and maps, you can just omit this component from app.js
 * Note: another common point to add <scripts>, <links> and <meta> tags is Page.js
 *       and Stripe script is added in public/index.html
 *
 * Note 2: When adding new external scripts/styles/fonts/etc.,
 *         if a Content Security Policy (CSP) is turned on, the new URLs
 *         should be whitelisted in the policy. Check: server/csp.js
 */
export const IncludeScripts = props => {
  const { marketplaceRootURL: rootURL, maps, analytics } = props?.config || {};
  const { googleAnalyticsId, plausibleDomains, facebookPixelId } = analytics;

  useEffect(() => {
    initializeMetaPixel(facebookPixelId);
  }, [facebookPixelId]);

  const { mapProvider, googleMapsAPIKey, mapboxAccessToken } = maps || {};
  const isGoogleMapsInUse = mapProvider === 'googleMaps';
  const isMapboxInUse = mapProvider === 'mapbox';

  // Add Google Analytics script if correct id exists (it should start with 'G-' prefix)
  // See: https://developers.google.com/analytics/devguides/collection/gtagjs
  const hasGoogleAnalyticsv4Id = googleAnalyticsId?.indexOf('G-') === 0;

  // Collect relevant map libraries
  let mapLibraries = [];
  let analyticsLibraries = [];

  if (isMapboxInUse) {
    // Not in the <head>: loadMapboxGL and loadMapboxSdk fetch it on demand with these.
    mapboxSettings = { accessToken: mapboxAccessToken, rootURL };
  } else if (isGoogleMapsInUse) {
    // Add Google Maps library
    mapLibraries.push(
      <script
        id={GOOGLE_MAPS_SCRIPT_ID}
        key="GoogleMapsApi"
        src={`https://maps.googleapis.com/maps/api/js?key=${googleMapsAPIKey}&libraries=places`}
        crossOrigin
      ></script>
    );
  }

  if (googleAnalyticsId && hasGoogleAnalyticsv4Id) {
    // Google Analytics: gtag.js
    // NOTE: This template is a single-page application (SPA).
    //       gtag.js sends initial page_view event after page load.
    //       but we need to handle subsequent events for in-app navigation.
    //       This is done in src/analytics/handlers.js
    analyticsLibraries.push(
      <script
        key="gtag.js"
        async
        src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`}
        crossOrigin
      ></script>
    );

    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      // Ensure that gtag function is found from window scope
      window.gtag = function gtag() {
        dataLayer.push(arguments);
      };
      gtag('js', new Date());
      gtag('config', googleAnalyticsId, {
        cookie_flags: 'SameSite=None;Secure',
      });
    }
  }

  if (plausibleDomains) {
    // If plausibleDomains is not an empty string, include their script too.
    analyticsLibraries.push(
      <script
        key="plausible"
        defer
        src="https://plausible.io/js/script.js"
        data-domain={plausibleDomains}
        crossOrigin
      ></script>
    );
  }

  const allScripts = [...analyticsLibraries, ...mapLibraries];
  return <Helmet>{allScripts}</Helmet>;
};
